import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/Home.vue'),
    meta: {
      title: '我爱纪录片 - 探索世界精彩纪录片',
      description: '最全面的纪录片在线平台，收录BBC、NHK、CCTV、Discovery、NG等10大主流频道精品纪录片'
    }
  },
  {
    path: '/search',
    name: 'Search',
    component: () => import('../views/Search.vue'),
    meta: {
      title: '搜索纪录片 - 我爱纪录片',
      description: '搜索您感兴趣的纪录片，支持按名称、导演、频道筛选'
    }
  },
  {
    path: '/documentary/:id',
    name: 'DocumentaryDetail',
    component: () => import('../views/DocumentaryDetail.vue'),
    meta: {
      title: '纪录片详情页 - 我爱纪录片',
      description: '查看纪录片详细信息、评分、简介等',
      minimalHeader: true
    }
  },
  {
    path: '/channel/:channel',
    name: 'Channel',
    component: () => import('../views/TVChannel.vue'),
    meta: {
      title: '频道纪录片 - 我爱纪录片',
      description: '浏览特定频道的纪录片资源',
      noPadding: true
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: {
      title: '关于网站 - 我爱纪录片',
      description: '了解我爱纪录片的使命、特色功能和联系方式',
      minimalHeader: true
    }
  },
  {
    path: '/vip',
    name: 'VIP',
    component: () => import('../views/VIP.vue'),
    meta: {
      title: '视频会员卡 - 我爱纪录片',
      description: '腾讯、B站、芒果、优酷、爱奇艺视频会员卡超值优惠'
    }
  },
  {
    path: '/admin/vip',
    name: 'VIPAdmin',
    component: () => import('../views/VIPAdmin.vue'),
    meta: {
      title: '卡密管理 - 我爱纪录片',
      description: '视频会员卡密批次管理',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/user',
    name: 'UserCenter',
    component: () => import('../views/UserCenter.vue'),
    meta: {
      title: '个人中心 - 我爱纪录片',
      description: '管理您的收藏、评分和个人设置',
      requiresAuth: true,
      minimalHeader: true
    }
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 路由守卫 - 设置页面标题和描述
router.beforeEach((to, _from, next) => {
  if (to.meta?.title) {
    document.title = to.meta.title as string
  }
  
  // 更新各种 meta 标签
  updateMetaTag('description', to.meta?.description as string)
  updateMetaTag('keywords', to.meta?.keywords as string)
  updateMetaTag('author', to.meta?.author as string)
  
  // 更新 Open Graph 标签
  updateMetaProperty('og:title', to.meta?.title as string)
  updateMetaProperty('og:description', to.meta?.description as string)
  updateMetaProperty('og:url', `https://www.52jlp.com${to.fullPath}`)
  updateMetaProperty('og:image', to.meta?.image as string)
  updateMetaProperty('og:type', to.meta?.ogType as string || 'website')
  
  // 更新 Twitter 标签
  updateMetaProperty('twitter:title', to.meta?.title as string)
  updateMetaProperty('twitter:description', to.meta?.description as string)
  updateMetaProperty('twitter:image', to.meta?.image as string)
  
  // 更新 canonical URL
  updateCanonicalLink(`https://www.52jlp.com${to.fullPath}`)
  
  // 添加结构化数据
  if (to.meta?.structuredData) {
    addStructuredData(to.meta.structuredData as object)
  }
  
  next()
})

// 辅助函数：更新 meta 标签
function updateMetaTag(name: string, content?: string) {
  if (!content) return
  
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = name
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

// 辅助函数：更新 meta property 标签
function updateMetaProperty(property: string, content?: string) {
  if (!content) return
  
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('property', property)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

// 辅助函数：更新 canonical 链接
function updateCanonicalLink(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = href
}

// 辅助函数：添加结构化数据
function addStructuredData(data: object) {
  // 移除旧的结构化数据
  const oldScript = document.querySelector('script[type="application/ld+json"][data-dynamic="true"]')
  if (oldScript) {
    oldScript.remove()
  }
  
  // 添加新的结构化数据
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.setAttribute('data-dynamic', 'true')
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

export default router